import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

import { CopyButton } from "~/components/copy-button";
import { DetailField, FieldGrid } from "~/components/detail-field";
import { PageHeader } from "~/components/page-header";
import { StatusBadge } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { errorMessage, isNotFound } from "~/lib/api";
import { EM_DASH, formatDate, formatDateTime, formatIDR, formatRelative, orDash } from "~/lib/format";
import {
  useAuditLog,
  useStorefront,
  useTransactions,
  type AuditLogEntry,
  type StorefrontResponse,
} from "~/lib/queries";
import { APIKeysTab } from "./-api-keys";
import { CallbackConfigTab } from "./-callback-config";
import { CallbackBadge, credentialLabel } from "./-callback-state";
import { ShopifyCredentialsTab } from "./-shopify-credentials";

interface StorefrontDetailSearch {
  /** Set by the create form so the new storefront lands with a next-steps card. */
  created?: boolean;
}

export const Route = createFileRoute("/_authed/storefronts/$storefrontId")({
  validateSearch: (raw: Record<string, unknown>): StorefrontDetailSearch => ({
    created: raw.created === true || raw.created === "true" ? true : undefined,
  }),
  component: StorefrontDetailPage,
});

const RECENT_LIMIT = 20;

/**
 * The hub the rest of the dashboard points back at.
 *
 * Built as a page with tabs rather than a flat card because M2 hangs callback
 * configuration and delivery history off it and M3 hangs API keys and Shopify
 * credentials off it. The empty tabs cost a few lines now and save rebuilding
 * this layout twice.
 */
function StorefrontDetailPage() {
  const { storefrontId } = Route.useParams();
  const { created } = Route.useSearch();
  const { data, error, isPending, refetch } = useStorefront(storefrontId);

  if (isPending) return <DetailSkeleton />;

  if (isNotFound(error)) {
    return (
      <NotFound
        body={
          <>
            No storefront has the id{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {storefrontId}
            </code>
            .
          </>
        }
      />
    );
  }

  if (error || !data) {
    return <NotFound body={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" render={<Link to="/storefronts" />}>
          <ArrowLeftIcon className="size-3.5" />
          All storefronts
        </Button>
      </div>

      <PageHeader
        title={data.storefront_name}
        description={orDash(data.shop_domain)}
        actions={<CallbackBadge storefront={data} />}
      />

      {created ? <NextSteps /> : null}

      <Overview storefront={data} />

      <Tabs defaultValue="transactions">
        <TabsList>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="callbacks">Callbacks</TabsTrigger>
          <TabsTrigger value="api-keys">API keys</TabsTrigger>
          <TabsTrigger value="shopify">Shopify credentials</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="pt-4">
          <RecentTransactions storefrontId={storefrontId} />
        </TabsContent>
        <TabsContent value="callbacks" className="pt-4">
          <CallbackConfigTab storefrontId={storefrontId} storefrontName={data.storefront_name} />
        </TabsContent>
        <TabsContent value="api-keys" className="pt-4">
          <APIKeysTab storefrontId={storefrontId} storefrontName={data.storefront_name} />
        </TabsContent>
        <TabsContent value="shopify" className="pt-4">
          <ShopifyCredentialsTab storefrontId={storefrontId} storefrontName={data.storefront_name} />
        </TabsContent>
      </Tabs>

      <ActivitySection storefrontId={storefrontId} />
    </div>
  );
}

const ACTIVITY_LIMIT = 10;

/**
 * Everything the audit log has ever recorded about this storefront.
 *
 * A per-object filter on the same endpoint the full audit log screen uses, not
 * a separate route — but deliberately target_id only, with no target_type.
 * audit_log has no storefront_id column of its own; a storefront's own
 * actions (storefront.create, shopify_credentials.*) are recorded with
 * target_type "storefront", while its callback configuration
 * (callback_config.set/clear/rotate_secret) is recorded under
 * target_type "callback_config" -- both using the storefront's id as
 * target_id. Fixing target_type here would silently drop the callback half of
 * a storefront's own history. It still cannot reach api_key.* rows, which key
 * on the API key's own id rather than the storefront's -- those stay findable
 * from the audit log's own storefront-scoped filters, just not from here.
 */
function ActivitySection({ storefrontId }: { storefrontId: string }) {
  const { data, error, isPending } = useAuditLog({
    target_id: storefrontId,
    limit: ACTIVITY_LIMIT,
  });

  const rows = data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Activity</CardTitle>
        <CardDescription>
          The last {ACTIVITY_LIMIT} admin mutations recorded against this storefront.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : error ? (
          <p className="text-sm text-muted-foreground">{errorMessage(error)}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recorded activity for this storefront yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {rows.map((row: AuditLogEntry) => (
              <li key={row.id} className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate">
                  <span className="font-mono text-[0.8125rem]">{row.action}</span>{" "}
                  <span className="text-muted-foreground">by {row.actor_email}</span>
                </span>
                <span
                  className="shrink-0 text-xs text-muted-foreground"
                  title={formatDateTime(row.created_at)}
                >
                  {formatRelative(row.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}

        <Button
          variant="ghost"
          size="sm"
          className="mt-1"
          render={
            <Link to="/audit-log" search={{ target_id: storefrontId }} />
          }
        >
          View all activity for this storefront
          <ArrowRightIcon className="size-3.5" />
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * The setup state, legible without opening a database.
 *
 * No secret is rendered anywhere here, and there is deliberately no masked field
 * with a reveal control: the API does not return the material (it is encrypted
 * at rest under CREDENTIALS_ENCRYPTION_KEY and carries json:"-"), and a row of
 * dots would imply the browser is holding something it never receives. The hint
 * is the tail of the secret -- enough to tell two rotations apart, useless for
 * forging a signature.
 */
function Overview({ storefront }: { storefront: StorefrontResponse }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGrid>
          <DetailField label="Storefront ID" copy={storefront.storefront_id} mono>
            {storefront.storefront_id}
          </DetailField>
          <DetailField
            label="Shop domain"
            copy={storefront.shop_domain}
            mono={Boolean(storefront.shop_domain)}
          >
            {orDash(storefront.shop_domain)}
          </DetailField>
          <DetailField label="Created">{formatDate(storefront.created_at)}</DetailField>

          <DetailField label="Callback">
            <CallbackDescription storefront={storefront} />
          </DetailField>
          <DetailField label="Callback URL" copy={storefront.callback_url} mono={false}>
            {orDash(storefront.callback_url)}
          </DetailField>
          <DetailField label="Shopify credentials">
            {storefront.has_shopify_credentials
              ? `configured (${credentialLabel(storefront)})`
              : "none"}
          </DetailField>
        </FieldGrid>
      </CardContent>
    </Card>
  );
}

function CallbackDescription({ storefront }: { storefront: StorefrontResponse }) {
  if (!storefront.has_callback_secret) {
    return <span className="text-muted-foreground">no signing secret set</span>;
  }

  return (
    <span>
      {storefront.callback_enabled ? "enabled" : "disabled"}
      {storefront.callback_secret_hint ? (
        <>
          , hint{" "}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
            {storefront.callback_secret_hint}
          </code>
        </>
      ) : null}
      {storefront.callback_secret_rotated_at
        ? `, rotated ${formatDate(storefront.callback_secret_rotated_at)}`
        : null}
    </span>
  );
}

/** The last few orders for this merchant, and a way through to all of them. */
function RecentTransactions({ storefrontId }: { storefrontId: string }) {
  const { data, error, isPending } = useTransactions({
    storefront_id: storefrontId,
    limit: RECENT_LIMIT,
  });

  const rows = data?.items ?? [];

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-base">Recent transactions</CardTitle>
        <CardDescription>The last {RECENT_LIMIT} orders for this storefront.</CardDescription>
      </CardHeader>

      <CardContent className="px-0 pb-0">
        {isPending ? (
          <div className="space-y-2 px-6 pb-6">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : error ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">{errorMessage(error)}</p>
        ) : rows.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">
            No transactions for this storefront yet.
          </p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-6">Order ID</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="pr-6">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.order_id}>
                    <TableCell className="pl-6">
                      <span className="flex items-center gap-1">
                        <Link
                          to="/transactions/$orderId"
                          params={{ orderId: row.order_id }}
                          className="font-mono text-[0.8125rem] text-primary hover:underline"
                        >
                          {row.order_id}
                        </Link>
                        <CopyButton value={row.order_id} label="order id" />
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatIDR(row.amount)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={row.payment_status} />
                    </TableCell>
                    <TableCell className="pr-6 text-muted-foreground">
                      {formatDateTime(row.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="border-t px-6 py-3">
              <Button
                variant="ghost"
                size="sm"
                render={
                  <Link to="/transactions" search={{ storefront_id: storefrontId }} />
                }
              >
                View all transactions
                <ArrowRightIcon className="size-3.5" />
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Shown once, after the create form lands here.
 *
 * It says plainly that the next two steps are not in the dashboard yet rather
 * than linking to a tab that would explain the same thing one click later.
 */
function NextSteps() {
  return (
    <Card className="border-info/30 bg-info/5">
      <CardHeader>
        <CardTitle className="text-base">Storefront created</CardTitle>
        <CardDescription>
          Two things usually come next. Setting a callback URL, so the merchant hears about
          payments, is on the Callbacks tab below — the first one also mints their signing
          secret, which is shown once. Minting an API key so they can call the API is not in
          this dashboard yet; that arrives in M3, and until then it is done the way it is done
          today.
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-44 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function NotFound({ body, onRetry }: { body: React.ReactNode; onRetry?: () => void }) {
  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Couldn't open this storefront</CardTitle>
          <CardDescription>{body ?? EM_DASH}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          {onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" render={<Link to="/storefronts" />}>
            Back to storefronts
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
